import { useCallback, useEffect, useRef, useState, type MutableRefObject, type PointerEvent as ReactPointerEvent } from "react";
import { encodeAlawFrame, encodeUlawFrame, G711_FRAME_SAMPLES, G711_RATE, PcmTo8k, VoiceConditioner } from "../lib/g711";
import { createTalkAudioContext, startPcmCapture } from "../lib/pcm-capture";
import { publishVoiceStats, voiceStats } from "../lib/audio-debug";
import { nativeVoiceAvailable, onNativeCapture, armNativeCapture, startNativeCapture, stopNativeCapture, setNativeTransmit, setNativeCodec, drainNativeCapture } from "../lib/native-voice";
import { hasNativeBridge } from "../lib/native-bridge";
import { APP_PAUSE_EVENT, APP_RESUME_EVENT } from "../lib/page-lifecycle";
import { pttLog } from "../lib/ptt-log";
import { masterSocketUrl } from "../api";
import { logLiveStep } from "../lib/live-trace";

const FRAME_SAMPLES = G711_FRAME_SAMPLES;
const MAX_WS_BUFFERED = FRAME_SAMPLES * 8;
const PRESSED_CLASS = "is-pressed";
const TALKING_CLASS = "is-talking";

export function talkWsUrl(deviceId: string): string {
  return masterSocketUrl(`/ws/talk/${encodeURIComponent(deviceId)}`);
}

export function micErrorMessage(err: unknown): string {
  const name = err && typeof err === "object" && "name" in err ? String((err as { name: string }).name) : "";
  if (!window.isSecureContext) return "Microphone needs HTTPS.";
  if (!navigator.mediaDevices?.getUserMedia) return "This browser cannot access the microphone.";
  if (name === "NotAllowedError" || name === "PermissionDeniedError") return "Microphone permission required";
  if (name === "NotFoundError" || name === "DevicesNotFoundError") return "No microphone found.";
  if (name === "NotReadableError") return "Microphone is already in use.";
  return "Could not start the microphone.";
}

function newCallId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

type TalkResources = {
  ws: WebSocket | null;
  stopMic: (() => void) | null;
  audioCtx: AudioContext | null;
  stream: MediaStream | null;
};

let activePttStop: (() => void) | null = null;

function closeAudioContext(ctx: AudioContext | null): void {
  if (!ctx || ctx.state === "closed") return;
  void ctx.close().catch(() => undefined);
}

function stopStream(stream: MediaStream | null): void {
  stream?.getTracks().forEach((t) => t.stop());
}

function paintPressed(btn: HTMLButtonElement | null, on: boolean): void {
  if (!btn) return;
  btn.classList.toggle(PRESSED_CLASS, on);
  btn.classList.toggle(TALKING_CLASS, on);
}

function requestMic(): Promise<MediaStream> {
  const micAudio: MediaTrackConstraints = {
    echoCancellation: true,
    noiseSuppression: false,
    autoGainControl: true,
    channelCount: { ideal: 1 },
    sampleRate: { ideal: 48000 },
  };
  const lowLatency = { ...micAudio, latency: { ideal: 0.01 } } as MediaTrackConstraints;
  return navigator.mediaDevices.getUserMedia({ audio: lowLatency, video: false }).catch(() =>
    navigator.mediaDevices.getUserMedia({ audio: micAudio, video: false }),
  );
}

export function usePushToTalk(opts: {
  deviceId: string;
  channel: number;
  online: boolean;
  hasLive: boolean;
  talkReady: boolean;
  showToast: (msg: string) => void;
}): {
  talking: boolean;
  buttonRef: MutableRefObject<HTMLButtonElement | null>;
  pttHandlers: {
    onPointerDown: (e: ReactPointerEvent<HTMLButtonElement>) => void;
    onPointerUp: (e: ReactPointerEvent<HTMLButtonElement>) => void;
    onPointerCancel: (e: ReactPointerEvent<HTMLButtonElement>) => void;
    onPointerLeave: (e: ReactPointerEvent<HTMLButtonElement>) => void;
    onContextMenu: (e: React.MouseEvent) => void;
    onClick: (e: React.MouseEvent) => void;
  };
} {
  const { deviceId, channel, online, hasLive, talkReady } = opts;
  const [talking, setTalking] = useState(false);
  const holdRef = useRef(false);
  const sessionRef = useRef(0);
  const pointerRef = useRef<number | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const resourcesRef = useRef<TalkResources>({ ws: null, stopMic: null, audioCtx: null, stream: null });
  const winUpRef = useRef<((ev: PointerEvent) => void) | null>(null);
  const optsRef = useRef(opts);
    const nativeRef = useRef(hasNativeBridge() && /Android/i.test(navigator.userAgent || ""));
  const codecRef = useRef<"pcma" | "pcmu">("pcma");
  const callIdRef = useRef(newCallId());
  const sendEnabledRef = useRef(false);
  const pttReadyWaiters = useRef<Array<() => void>>([]);
  optsRef.current = opts;

  const signalPttReady = useCallback(() => {
    const waiters = pttReadyWaiters.current.splice(0);
    for (const w of waiters) w();
  }, []);

  const waitPttReady = useCallback((ms: number) => {
    return new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        resolve();
      };
      pttReadyWaiters.current.push(finish);
      window.setTimeout(finish, ms);
    });
  }, []);

  const detachWindow = useCallback(() => {
    if (!winUpRef.current) return;
    window.removeEventListener("pointerup", winUpRef.current, true);
    window.removeEventListener("pointercancel", winUpRef.current, true);
    winUpRef.current = null;
  }, []);

  const stopMicOnly = useCallback(() => {
    resourcesRef.current.stopMic?.();
    resourcesRef.current.stopMic = null;
    stopStream(resourcesRef.current.stream);
    resourcesRef.current.stream = null;
    closeAudioContext(resourcesRef.current.audioCtx);
    resourcesRef.current.audioCtx = null;
    onNativeCapture(null);
    setNativeTransmit(false);
  }, []);

  const stopTalk = useCallback(() => {
    const wasHeld = holdRef.current;
    holdRef.current = false;
    sessionRef.current += 1;
    pointerRef.current = null;
    detachWindow();
    if (activePttStop === stopTalk) activePttStop = null;
    paintPressed(buttonRef.current, false);
    setTalking(false);
    stopMicOnly();
    pttReadyWaiters.current.splice(0);
    // Must close intercom (0x9102 cmd 4) and restore live AV — previously only
    // happened on WS unmount, leaving cameras stuck in dataType-2 after release.
    if (wasHeld) {
      const sock = resourcesRef.current.ws;
      if (sock && sock.readyState === WebSocket.OPEN) {
        try {
          sock.send(JSON.stringify({ action: "stop" }));
        } catch {
          /* ignore */
        }
      }
      pttLog("stop");
    }
  }, [detachWindow, stopMicOnly]);

  useEffect(() => {
    void nativeVoiceAvailable().then((ok) => {
      nativeRef.current = ok;
      if (ok) publishVoiceStats({ captureMode: "native", captureRate: 8000, captureBlock: 160 });
    });
  }, []);

  useEffect(() => {
    if (!talkReady || !online || !hasLive || !deviceId) return;
    let closed = false;
    const callId = newCallId();
    callIdRef.current = callId;
    logLiveStep({ vehicleId: deviceId, channel, step: "talk_ws" });
    const ws = new WebSocket(talkWsUrl(deviceId));
    ws.binaryType = "arraybuffer";
    resourcesRef.current.ws = ws;
    sendEnabledRef.current = false;
    ws.onopen = () => {
      if (closed) return;
      logLiveStep({ vehicleId: deviceId, channel, step: "talk_ws_open" });
      try {
        ws.send(JSON.stringify({ action: "start", channel, callId }));
        sendEnabledRef.current = true;
        publishVoiceStats({ ws: "talk-armed", callId });
        pttLog("intercom-armed", { deviceId, channel, callId });
        if (nativeRef.current) void armNativeCapture(codecRef.current);
      } catch {
        sendEnabledRef.current = false;
      }
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data !== "string") return;
      try {
        const msg = JSON.parse(ev.data) as {
          ok?: boolean;
          type?: string;
          codec?: string;
          error?: string;
          t?: number;
          callId?: string;
        };
        if (msg.codec === "pcmu" || msg.codec === "pcma") {
          codecRef.current = msg.codec;
          if (nativeRef.current) setNativeCodec(msg.codec);
        }
        if (msg.type === "talking") publishVoiceStats({ pttTalkingMs: 0, callId });
        if (msg.type === "ptt-ack" && typeof msg.t === "number") {
          publishVoiceStats({ serverReceiveMs: msg.t, callId: typeof msg.callId === "string" ? msg.callId : callId });
        }
        if (msg.type === "ptt-ready") {
          signalPttReady();
        }
        if (msg.ok === false && msg.error && msg.error !== "unknown_action") {
          sendEnabledRef.current = false;
          optsRef.current.showToast("Two-way audio is unavailable for this device");
        }
      } catch {
        /* hello */
      }
    };
    ws.onerror = () => {
      logLiveStep({ vehicleId: deviceId, channel, step: "talk_ws", error: "connect_failed" });
    };
    ws.onclose = () => {
      logLiveStep({ vehicleId: deviceId, channel, step: "disconnect", error: "talk_ws" });
      if (resourcesRef.current.ws === ws) resourcesRef.current.ws = null;
      sendEnabledRef.current = false;
    };
    return () => {
      closed = true;
      sendEnabledRef.current = false;
      if (resourcesRef.current.ws === ws) resourcesRef.current.ws = null;
      try {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action: "stop" }));
      } catch {
        /* ignore */
      }
      try {
        ws.close();
      } catch {
        /* ignore */
      }
      void stopNativeCapture();
    };
  }, [deviceId, channel, online, hasLive, talkReady]);

  useEffect(() => () => {
    stopTalk();
  }, [stopTalk]);

  useEffect(() => {
    const halt = () => {
      if (holdRef.current) stopTalk();
    };
    const onVis = () => {
      if (document.visibilityState === "hidden") halt();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", halt);
    window.addEventListener(APP_PAUSE_EVENT, halt);
    window.addEventListener(APP_RESUME_EVENT, onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", halt);
      window.removeEventListener(APP_PAUSE_EVENT, halt);
      window.removeEventListener(APP_RESUME_EVENT, onVis);
    };
  }, [stopTalk]);

  useEffect(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const block = (ev: TouchEvent) => {
      ev.preventDefault();
    };
    btn.addEventListener("touchstart", block, { passive: false });
    btn.addEventListener("touchmove", block, { passive: false });
    return () => {
      btn.removeEventListener("touchstart", block);
      btn.removeEventListener("touchmove", block);
    };
  }, [deviceId]);

  const sendFrame = useCallback((encoded: Uint8Array, tGesture: number, markFirst: { current: boolean }) => {
    const sock = resourcesRef.current.ws;
    if (!holdRef.current || !sock || sock.readyState !== WebSocket.OPEN || !sendEnabledRef.current) return false;
    if (encoded.length !== FRAME_SAMPLES) return false;
    if (sock.bufferedAmount > MAX_WS_BUFFERED) {
      publishVoiceStats({ pttDropped: voiceStats().pttDropped + 1 });
      return false;
    }
    sock.send(encoded);
    publishVoiceStats({ sendAt: Date.now(), pttPackets: voiceStats().pttPackets + 1 });
    if (markFirst.current) {
      markFirst.current = false;
      const dt = performance.now() - tGesture;
      pttLog("first-send", { dtMs: Math.round(dt), bytes: encoded.length, codec: codecRef.current, native: nativeRef.current });
      publishVoiceStats({ pttFirstSendMs: dt, pttFirstEncodeMs: dt, codec: codecRef.current, pttPackets: 1 });
    }
    return true;
  }, []);

  const startBrowserCapture = useCallback(async (audioCtx: AudioContext | null, myGen: number, tGesture: number) => {
    const { showToast: toast } = optsRef.current;
    const stillHeld = () => holdRef.current && sessionRef.current === myGen;
    let stream: MediaStream;
    try {
      stream = await requestMic();
    } catch (err) {
      if (sessionRef.current === myGen) toast(micErrorMessage(err));
      return;
    }
    publishVoiceStats({ pttMicMs: performance.now() - tGesture });
    if (!stillHeld()) {
      stopStream(stream);
      return;
    }
    resourcesRef.current.stream = stream;
    resourcesRef.current.audioCtx = audioCtx;
    const resampler = new PcmTo8k(audioCtx?.sampleRate ?? G711_RATE);
    const voice = new VoiceConditioner();
    let leftover = new Float32Array(0);
    let firstPcm = true;
    const firstSend = { current: true };
    try {
      const capture = await startPcmCapture(
        stream,
        (input, sampleRate) => {
          if (!stillHeld()) return;
          if (firstPcm) {
            firstPcm = false;
            publishVoiceStats({ pttFirstPcmMs: performance.now() - tGesture, captureRate: sampleRate, captureAt: Date.now() });
          }
          if (sampleRate !== resampler.inputRate) resampler.setInputRate(sampleRate);
          const down = resampler.process(input);
          if (!down.length) return;
          const merged = new Float32Array(leftover.length + down.length);
          merged.set(leftover);
          merged.set(down, leftover.length);
          let offset = 0;
          while (offset + FRAME_SAMPLES <= merged.length) {
            const frame = merged.subarray(offset, offset + FRAME_SAMPLES);
            voice.process(frame);
            const encoded = codecRef.current === "pcmu" ? encodeUlawFrame(frame) : encodeAlawFrame(frame);
            sendFrame(encoded, tGesture, firstSend);
            offset += FRAME_SAMPLES;
          }
          leftover = merged.slice(offset);
        },
        audioCtx,
      );
      if (!stillHeld()) {
        capture.stop();
        return;
      }
      resourcesRef.current.stopMic = () => capture.stop();
      publishVoiceStats({ captureMode: capture.mode, captureRate: capture.sampleRate, captureBlock: capture.blockSize });
    } catch (err) {
      stopStream(stream);
      if (sessionRef.current === myGen) toast(err instanceof Error ? err.message : "Could not start the microphone.");
    }
  }, [sendFrame]);

  const startNativeMic = useCallback(async (myGen: number, tGesture: number) => {
    const stillHeld = () => holdRef.current && sessionRef.current === myGen;
    const firstSend = { current: true };
    let pumping = true;
    let gotEvent = false;
    let useEvents = true;
    onNativeCapture((frame) => {
      if (!stillHeld() || !pumping || !useEvents) return;
      gotEvent = true;
      publishVoiceStats({ captureAt: frame.t, captureMode: "native", captureRate: 8000, captureBlock: 160 });
      if (firstSend.current) {
        publishVoiceStats({ pttFirstPcmMs: performance.now() - tGesture });
      }
      sendFrame(frame.g711, tGesture, firstSend);
    });
    setNativeTransmit(true);
    const ok = await startNativeCapture(codecRef.current);
    publishVoiceStats({ pttMicMs: performance.now() - tGesture });
    if (!ok) {
      pumping = false;
      onNativeCapture(null);
      setNativeTransmit(false);
      return false;
    }
    if (!stillHeld()) {
      pumping = false;
      onNativeCapture(null);
      setNativeTransmit(false);
      return false;
    }
    resourcesRef.current.stopMic = () => {
      pumping = false;
      onNativeCapture(null);
      setNativeTransmit(false);
    };
    const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));
    void (async () => {
      const started = performance.now();
      await wait(120);
      if (gotEvent) return;
      useEvents = false;
      let got = false;
      while (pumping && stillHeld()) {
        const frames = await drainNativeCapture();
        if (!stillHeld() || !pumping) break;
        if (frames.length > 0) {
          got = true;
          publishVoiceStats({ captureAt: Date.now(), captureMode: "native", captureRate: 8000, captureBlock: 160 });
          if (firstSend.current) publishVoiceStats({ pttFirstPcmMs: performance.now() - tGesture });
          for (const frame of frames) sendFrame(frame, tGesture, firstSend);
        } else if (!got && performance.now() - started > 450) {
          pumping = false;
          onNativeCapture(null);
          setNativeTransmit(false);
          if (stillHeld()) await startBrowserCapture(null, myGen, tGesture);
          return;
        }
        await wait(12);
      }
    })();
    return true;
  }, [sendFrame, startBrowserCapture]);

  const startTalk = useCallback(async (audioCtx: AudioContext | null, myGen: number, tGesture: number) => {
    const { online: isOnline, hasLive: live, talkReady: ready, showToast: toast } = optsRef.current;
    const stillHeld = () => holdRef.current && sessionRef.current === myGen;
    const abortVisual = () => {
      if (sessionRef.current !== myGen) return;
      holdRef.current = false;
      pointerRef.current = null;
      detachWindow();
      if (activePttStop === stopTalk) activePttStop = null;
      paintPressed(buttonRef.current, false);
      setTalking(false);
    };
    if (!isOnline) {
      abortVisual();
      toast("Camera is offline");
      return;
    }
    if (!live) {
      abortVisual();
      toast("Reconnecting camera...");
      return;
    }
    if (!ready) {
      abortVisual();
      toast("Two-way audio is unavailable for this device");
      return;
    }
    if (!stillHeld()) return;

    const sock = resourcesRef.current.ws;
    const callId = callIdRef.current;
    if (sock && sock.readyState === WebSocket.OPEN) {
      try {
        sock.send(JSON.stringify({ action: "ptt", callId, t: Date.now() }));
      } catch {
        /* ignore */
      }
    }

    // Wait for server ptt-ready (JT1078 socket) with a tight cap — no fixed 280ms lag.
    await waitPttReady(120);
    if (!stillHeld()) return;

    if (nativeRef.current) {
      const ok = await startNativeMic(myGen, tGesture);
      if (ok || !stillHeld()) return;
    }
    await startBrowserCapture(audioCtx, myGen, tGesture);
  }, [detachWindow, startBrowserCapture, startNativeMic, stopTalk, waitPttReady]);

  const onPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    if (e.pointerType !== "touch") e.preventDefault();
    if (holdRef.current && pointerRef.current === e.pointerId) return;
    if (holdRef.current && pointerRef.current != null && pointerRef.current !== e.pointerId) return;

    const btn = e.currentTarget;
    try {
      btn.setPointerCapture(e.pointerId);
    } catch {
      /* Chrome/iOS may reject capture */
    }

    if (activePttStop && activePttStop !== stopTalk) activePttStop();

    pointerRef.current = e.pointerId;
    holdRef.current = true;
    const myGen = sessionRef.current + 1;
    sessionRef.current = myGen;
    activePttStop = stopTalk;
    paintPressed(btn, true);
    setTalking(true);

    const tGesture = performance.now();
    const ctx = nativeRef.current ? null : createTalkAudioContext();
    if (ctx) void ctx.resume();
    pttLog("gesture", { native: nativeRef.current, armed: sendEnabledRef.current, callId: callIdRef.current });
    publishVoiceStats({
      pttGestureAt: Date.now(),
      pttWsOpenMs: sendEnabledRef.current ? 0 : -1,
      pttMicMs: 0,
      pttFirstPcmMs: 0,
      pttFirstEncodeMs: 0,
      pttFirstSendMs: 0,
      pttPackets: 0,
      pttDropped: 0,
      callId: callIdRef.current,
    });

    detachWindow();
    const pointerId = e.pointerId;
    const onWinUp = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      ev.preventDefault();
      detachWindow();
      stopTalk();
    };
    winUpRef.current = onWinUp;
    window.addEventListener("pointerup", onWinUp, { capture: true });
    window.addEventListener("pointercancel", onWinUp, { capture: true });

    void startTalk(ctx, myGen, tGesture);
  };

  const endFromPointer = (e: ReactPointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (pointerRef.current != null && e.pointerId !== pointerRef.current) return;
    stopTalk();
  };

  return {
    talking,
    buttonRef,
    pttHandlers: {
      onPointerDown,
      onPointerUp: endFromPointer,
      onPointerCancel: endFromPointer,
      onPointerLeave: (e) => {
        if (e.pointerType === "mouse" && holdRef.current) stopTalk();
      },
      onContextMenu: (e) => {
        e.preventDefault();
        e.stopPropagation();
      },
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
      },
    },
  };
}
